/**
 * DEV-ONLY: Route-scoped devtools session (localStorage + optional URL export).
 * - Keyed by pathname: devtools:session:${window.location.pathname}
 * - Seeds from query params on load (overlay + inspect)
 * - Autosaves to localStorage; does NOT keep URL in sync while editing
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const DevtoolsSessionContext = createContext(null);

function getStorageKey(pathname) {
  return `devtools:session:${pathname}`;
}

export function getDefaultDevtoolsSession() {
  return {
    overlay: {
      enabled: false,
      file: "",
      visible: true,
      opacity: 0.5,
      offsetX: 0,
      offsetY: 0,
      mode: "normal", // "normal" | "difference"
      widthPx: null,
      heightPx: null,
      lockAspectRatio: true,
    },
    inspector: {
      enabled: false,
      selectedElement: null, // { strategy: "data-devtools-id"|"id", value: string } | null
      tempEdits: {}, // { padding, margin, width, maxWidth, fontSize, lineHeight, gap, borderRadius, mediaComposition? }
    },
  };
}

function safeJsonParse(str) {
  if (!str || typeof str !== "string") return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function mergeSession(base, patch) {
  if (!patch || typeof patch !== "object") return base;
  return {
    ...base,
    overlay: { ...base.overlay, ...(patch.overlay || {}) },
    inspector: { ...base.inspector, ...(patch.inspector || {}) },
  };
}

function parseFloatParam(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseModeParam(value, fallback) {
  if (value === "difference" || value === "normal") return value;
  return fallback;
}

function parseUrlSeed(search) {
  const defaults = getDefaultDevtoolsSession();
  const sp = new URLSearchParams(search || "");

  const overlayFile = sp.get("overlay");
  const inspectOn = sp.get("inspect") === "1";

  const seed = getDefaultDevtoolsSession();

  if (overlayFile) {
    seed.overlay.enabled = true;
    seed.overlay.file = overlayFile;
    seed.overlay.visible = true;
    seed.overlay.opacity = parseFloatParam(sp.get("opacity"), defaults.overlay.opacity);
    seed.overlay.offsetX = parseFloatParam(sp.get("x"), defaults.overlay.offsetX);
    seed.overlay.offsetY = parseFloatParam(sp.get("y"), defaults.overlay.offsetY);
    seed.overlay.mode = parseModeParam(sp.get("mode"), defaults.overlay.mode);
    const w = parseFloatParam(sp.get("w"), 0);
    const h = parseFloatParam(sp.get("h"), 0);
    seed.overlay.widthPx = w > 0 ? Math.round(w) : null;
    seed.overlay.heightPx = h > 0 ? Math.round(h) : null;
    const lockParam = sp.get("lockAspect");
    if (lockParam === "0") seed.overlay.lockAspectRatio = false;
  }

  if (inspectOn) {
    seed.inspector.enabled = true;
  }

  return seed;
}

function buildShareUrlForCurrentLocation(location, session) {
  const url = new URL(window.location.href);
  url.pathname = location.pathname;
  url.hash = location.hash || "";

  const sp = new URLSearchParams();

  if (session?.overlay?.enabled && session.overlay.file) {
    sp.set("overlay", session.overlay.file);
    sp.set("opacity", String(session.overlay.opacity ?? 0.5));
    sp.set("x", String(session.overlay.offsetX ?? 0));
    sp.set("y", String(session.overlay.offsetY ?? 0));
    if (session.overlay.mode && session.overlay.mode !== "normal") sp.set("mode", session.overlay.mode);
    if (session.overlay.widthPx != null) sp.set("w", String(session.overlay.widthPx));
    if (session.overlay.heightPx != null) sp.set("h", String(session.overlay.heightPx));
    if (session.overlay.lockAspectRatio === false) sp.set("lockAspect", "0");
  }

  if (session?.inspector?.enabled) {
    sp.set("inspect", "1");
  }

  url.search = sp.toString() ? `?${sp.toString()}` : "";
  return url.toString();
}

export function DevtoolsSessionProvider({ children }) {
  const location = useLocation();
  const storageKey = useMemo(() => getStorageKey(location.pathname), [location.pathname]);

  const [session, setSession] = useState(() => getDefaultDevtoolsSession());
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);

  // Hydrate whenever pathname changes (route-scoped sessions).
  useEffect(() => {
    const defaults = getDefaultDevtoolsSession();
    const stored = safeJsonParse(window.localStorage.getItem(storageKey));
    const seededFromUrl = parseUrlSeed(location.search);

    // Priority: defaults -> localStorage -> URL seed (URL wins on load).
    const merged = mergeSession(mergeSession(defaults, stored), seededFromUrl);
    setSession(merged);
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, location.search]);

  // Autosave with a tiny debounce.
  useEffect(() => {
    if (!hydratedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(session));
      } catch (_) {
        // ignore quota/private-mode failures
      }
    }, 150);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [session, storageKey]);

  const updateSession = useCallback((updater) => {
    setSession((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      return mergeSession(prev, next);
    });
  }, []);

  const resetSession = useCallback(() => {
    const defaults = getDefaultDevtoolsSession();
    setSession(defaults);
    try {
      window.localStorage.removeItem(storageKey);
    } catch (_) {
      // ignore
    }
  }, [storageKey]);

  const copyShareUrl = useCallback(async () => {
    const shareUrl = buildShareUrlForCurrentLocation(location, session);
    try {
      await navigator.clipboard.writeText(shareUrl);
      return { ok: true, url: shareUrl };
    } catch (e) {
      // Best-effort fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        return { ok: true, url: shareUrl };
      } catch {
        return { ok: false, url: shareUrl, error: e };
      }
    }
  }, [location, session]);

  const value = useMemo(
    () => ({
      session,
      setSession: updateSession,
      resetSession,
      copyShareUrl,
      buildShareUrl: () => buildShareUrlForCurrentLocation(location, session),
    }),
    [session, updateSession, resetSession, copyShareUrl, location]
  );

  return <DevtoolsSessionContext.Provider value={value}>{children}</DevtoolsSessionContext.Provider>;
}

export function useDevtoolsSession() {
  return useContext(DevtoolsSessionContext);
}

